import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "@env";
import { Observable } from "rxjs";
import type {
  AffiliateData,
  User,
  UserBillingAddress,
  UserCompanyAddressInfo,
  UserKyc,
  UserPayoutMethods,
  UserUpdateData,
} from "@models";
import type { LegacyResponse } from "./response.model";

@Injectable({ providedIn: "root" })
export class UserService {
  private _usersApiUrl = environment.usersApiUrl;

  constructor(private _httpClient: HttpClient) {}

  public getCurrentUser() {
    return this._httpClient.get<User>(`${this._usersApiUrl}/me`);
  }

  public isMissingKycData() {
    return this._httpClient.get<boolean>(`${this._usersApiUrl}/me/is-missing-kyc-data`);
  }

  public updateUserAddress(data: UserBillingAddress) {
    return this._httpClient.put<LegacyResponse<User>>(
      `${this._usersApiUrl}/me/update-address`,
      data,
    );
  }

  public updateUserCompanyAddressInfo(data: UserCompanyAddressInfo) {
    return this._httpClient.put<LegacyResponse<User>>(
      `${this._usersApiUrl}/me/update-address`,
      data,
    );
  }

  public updateUserInfo(data: UserUpdateData) {
    return this._httpClient.put<LegacyResponse<User>>(`${this._usersApiUrl}/me/update`, data);
  }

  public updatePassword(data: { oldPassword: string; newPassword: string }) {
    return this._httpClient.put<LegacyResponse<User>>(
      `${this._usersApiUrl}/me/update-password`,
      data,
    );
  }

  public updateEmail(data: { currentPassword: string; newEmailAddress: string }) {
    return this._httpClient.put<LegacyResponse<null>>(`${this._usersApiUrl}/me/update-email`, data);
  }

  public getUserKycStatus() {
    return this._httpClient.get<UserKyc>(`${this._usersApiUrl}/me/kyc`);
  }

  public generateKycSdkToken() {
    return this._httpClient.post<{ accessToken: string }>(
      `${this._usersApiUrl}/me/kyc/start`,
      null,
    );
  }

  public getUserPayoutMethods() {
    return this._httpClient.get<UserPayoutMethods>(`${this._usersApiUrl}/me/payout-methods`);
  }

  public updateCryptoPayoutMethod(data: Pick<UserPayoutMethods, "erc20AddressUsdc">) {
    return this._httpClient.put<UserPayoutMethods>(
      `${this._usersApiUrl}/me/payout-methods/crypto`,
      data,
    );
  }

  public updateRevolutPayoutMethod(data: Pick<UserPayoutMethods, "swiftCode" | "iban">) {
    return this._httpClient.put<UserPayoutMethods>(
      `${this._usersApiUrl}/me/payout-methods/revolut`,
      data,
    );
  }

  public updateBankRoutingNumber(data: { routingNumber: string }): Observable<UserPayoutMethods> {
    return this._httpClient.put<UserPayoutMethods>(
      `${this._usersApiUrl}/me/payout-methods/bank/routing-number`,
      data,
    );
  }

  public getAffiliateDetails() {
    return this._httpClient.get<AffiliateData>(`${this._usersApiUrl}/affiliates/me`);
  }

  public requestPayout(data: { payoutMethod: string; requestedAmount: number }) {
    return this._httpClient.put<LegacyResponse<null>>(
      `${this._usersApiUrl}/affiliates/me/request-payout`,
      data,
    );
  }

  public triggerCheckoutReached() {
    return this._httpClient.post<any>(`${this._usersApiUrl}/me/reached-checkout`, null);
  }

  public verifyEmail() {
    return this._httpClient.post<void>(`${this._usersApiUrl}/me/verify-email`, null);
  }
}
